import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error handler utility
const handleApiError = (error, context) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, error?.message || error);
  }
};

/**
 * Hook to fetch the video script
 */
export function useScript() {
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchScript = async () => {
      try {
        const response = await axios.get(`${API}/script`);
        if (isMounted) {
          setScript(response.data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || 'Failed to fetch script');
          handleApiError(err, 'useScript');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchScript();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { script, loading, error };
}

/**
 * Hook to fetch chapters
 */
export function useChapters() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchChapters = async () => {
      try {
        const response = await axios.get(`${API}/chapters`);
        if (isMounted) {
          setChapters(response.data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || 'Failed to fetch chapters');
          handleApiError(err, 'useChapters');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchChapters();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { chapters, loading, error };
}

/**
 * Hook to manage generation status and polling
 */
export function useGenerations() {
  const [audioStatus, setAudioStatus] = useState({});
  const [videoStatus, setVideoStatus] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);

  const fetchGenerations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/generations`);
      const generations = response.data;
      
      const audio = {};
      const video = {};
      
      generations.forEach(gen => {
        if (gen.type === "audio") {
          audio[gen.chapter_id] = gen;
        } else if (gen.type === "video") {
          video[gen.chapter_id] = gen;
        }
      });
      
      setAudioStatus(audio);
      setVideoStatus(video);
      setError(null);
    } catch (err) {
      handleApiError(err, 'fetchGenerations');
      setError(err?.message || 'Failed to fetch generations');
    }
  }, []);

  // Check if any generation is in progress
  useEffect(() => {
    const audioProcessing = Object.values(audioStatus).some(s => s?.status === "processing");
    const videoProcessing = Object.values(videoStatus).some(s => s?.status === "processing");
    setIsGenerating(audioProcessing || videoProcessing);
  }, [audioStatus, videoStatus]);

  // Poll for generation status
  useEffect(() => {
    if (isGenerating) {
      pollIntervalRef.current = setInterval(fetchGenerations, 3000);
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isGenerating, fetchGenerations]);

  // Initial fetch
  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]);

  const generateAllAudio = useCallback(async (voice = "fable") => {
    try {
      setIsGenerating(true);
      await axios.post(`${API}/generate/all-audio`, null, { params: { voice } });
      await fetchGenerations();
    } catch (err) {
      handleApiError(err, 'generateAllAudio');
      setError(err?.message || 'Failed to generate audio');
    }
  }, [fetchGenerations]);

  const generateChapterAudio = useCallback(async (chapterId, voice = "fable") => {
    try {
      await axios.post(`${API}/generate/audio`, { chapter_id: chapterId, voice });
      setIsGenerating(true);
      await fetchGenerations();
    } catch (err) {
      handleApiError(err, 'generateChapterAudio');
      setError(err?.message || 'Failed to generate chapter audio');
    }
  }, [fetchGenerations]);

  return {
    audioStatus,
    videoStatus,
    isGenerating,
    error,
    generateAllAudio,
    generateChapterAudio,
    refreshGenerations: fetchGenerations
  };
}

/**
 * Hook to manage audio playback state
 */
export function useAudioState() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const reset = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  return {
    isPlaying,
    setIsPlaying,
    isMuted,
    setIsMuted,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    reset
  };
}

/**
 * Hook to manage audio element events
 */
export function useAudioEvents(audioRef, setCurrentTime, setDuration, setIsPlaying, onEnded) {
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, [audioRef, setCurrentTime]);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, [audioRef, setDuration]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    if (onEnded) {
      onEnded();
    }
  }, [setIsPlaying, onEnded]);

  return { handleTimeUpdate, handleLoadedMetadata, handleEnded };
}

/**
 * Hook to manage audio playback controls
 */
export function useAudioControls(audioRef, isPlaying, setIsPlaying, isMuted, setIsMuted, duration) {
  const play = useCallback(async () => {
    if (audioRef.current) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        handleApiError(err, 'audioPlay');
      }
    }
  }, [audioRef, setIsPlaying]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [audioRef, setIsPlaying]);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(prev => !prev);
  }, [audioRef, isMuted, setIsMuted]);

  const seek = useCallback((percentage) => {
    if (audioRef.current && duration > 0) {
      audioRef.current.currentTime = percentage * duration;
    }
  }, [audioRef, duration]);

  return { play, pause, togglePlay, toggleMute, seek };
}

/**
 * Combined hook for audio player functionality
 */
export function useAudioPlayer(chapters, currentChapter, audioStatus) {
  const audioRef = useRef(null);
  
  const {
    isPlaying, setIsPlaying,
    isMuted, setIsMuted,
    currentTime, setCurrentTime,
    duration, setDuration,
    reset
  } = useAudioState();

  const chapter = chapters[currentChapter];
  const hasAudio = chapter && !chapter.is_intro && audioStatus[chapter.id]?.status === "completed";

  // Reset when chapter changes
  useEffect(() => {
    reset();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [currentChapter, reset]);

  const { handleTimeUpdate, handleLoadedMetadata, handleEnded } = useAudioEvents(
    audioRef, setCurrentTime, setDuration, setIsPlaying, null
  );

  const { play, pause, togglePlay, toggleMute, seek } = useAudioControls(
    audioRef, isPlaying, setIsPlaying, isMuted, setIsMuted, duration
  );

  return {
    audioRef,
    isPlaying,
    isMuted,
    currentTime,
    duration,
    hasAudio,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded,
    togglePlay,
    toggleMute,
    seek,
    play,
    pause
  };
}
